import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, refreshAccessToken } from 'auth/authApi';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from 'auth/tokenStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true); // reconnexion automatique en cours
  const [error, setError] = useState('');

  const logout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
  }, []);

  // Au démarrage de l'application : connexion automatique si un jeton valide (ou
  // un refresh token) est déjà stocké, sans redemander l'identifiant/mot de passe.
  useEffect(() => {
    if (getAccessToken()) {
      setIsAuthenticated(true);
      setCheckingSession(false);
      return;
    }
    const refresh = getRefreshToken();
    if (!refresh) {
      setCheckingSession(false);
      return;
    }
    refreshAccessToken(refresh)
      .then((tokens) => {
        setTokens(tokens);
        setIsAuthenticated(true);
      })
      .catch(() => clearTokens())
      .finally(() => setCheckingSession(false));
  }, []);

  // Déconnexion forcée déclenchée par l'intercepteur api.js (jeton expiré et
  // rafraîchissement impossible)
  useEffect(() => {
    window.addEventListener('auth:logout', logout);
    return () => window.removeEventListener('auth:logout', logout);
  }, [logout]);

  const login = async (username, password) => {
    setError('');
    try {
      const tokens = await apiLogin(username, password);
      setTokens(tokens);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      setError('Identifiant ou mot de passe incorrect.');
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, checkingSession, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
